// Types
type Severity = 'LOW' | 'MEDIUM' | 'HIGH';

export type FinalError = {
    tags: {
        page: string;
        component: string;
        action: string;
        severity: Severity;
    };
    extra: {
        message: string;
    };
};

type ErrorDefinition = {
    severity?: Severity;
    message?: string;
};

export default <Page extends string, Component extends string>(page: Page) => {
    return <const T extends Record<Component, Record<string, ErrorDefinition>>>(
        errors: T
    ): {
        [C in keyof T]: {
            [A in keyof T[C]]: FinalError;
        };
    } => {
        const result = {} as {
            [C in keyof T]: {
                [A in keyof T[C]]: FinalError;
            };
        };

        for (const component of Object.keys(errors) as (keyof T)[]) {
            const actions = errors[component];
            const componentErrors = {} as {
                [A in keyof T[typeof component]]: FinalError;
            };

            for (const action of Object.keys(actions) as (keyof typeof actions)[]) {
                const def = actions[action];

                componentErrors[action] = {
                    tags: {
                        page: page ?? 'UNKNOWN_PAGE',
                        component: component as string,
                        action: action as string,
                        severity: def?.severity ?? 'MEDIUM',
                    },
                    extra: {
                        message: def?.message ?? 'UNKNOWN_ERROR',
                    },
                };
            }

            result[component] = componentErrors;
        }

        return result;
    };
};
